import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Zap,
  Building,
  AlertTriangle,
  RefreshCw,
  CheckCircle,
  XCircle,
  MinusCircle,
  Calendar,
  Clock,
  Users,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { analyticsAPI } from '../services/api';
import { formatCurrency } from '../utils/helpers';
import toast from 'react-hot-toast';

/* ─── helpers ─────────────────────────────────────────────────────── */
const CELL_COLORS = {
  paid:    'bg-emerald-500',
  partial: 'bg-amber-400',
  unpaid:  'bg-red-500',
  no_data: 'bg-secondary-700',
  future:  'bg-secondary-800',
};
const CELL_LABEL = {
  paid: 'Paid', partial: 'Partial', unpaid: 'Unpaid', no_data: 'No data', future: '—',
};

const SEVERITY_COLORS = {
  critical: 'bg-red-500/20 text-red-400 border border-red-500/30',
  high:     'bg-orange-500/20 text-orange-400 border border-orange-500/30',
  medium:   'bg-amber-500/20 text-amber-400 border border-amber-500/30',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-secondary-800 border border-secondary-700 rounded-lg p-3 shadow-xl text-sm">
      <p className="text-secondary-300 font-medium mb-2">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="flex items-center gap-2">
          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
          {p.name}: <span className="font-semibold">{typeof p.value === 'number' && p.name?.toLowerCase().includes('%') ? `${p.value.toFixed(1)}%` : p.value}</span>
        </p>
      ))}
    </div>
  );
};

/* ─── component ───────────────────────────────────────────────────── */
const AnalyticsPage = () => {
  const [occupancy,   setOccupancy]   = useState([]);
  const [yoy,         setYoy]         = useState(null);
  const [behavior,    setBehavior]    = useState([]);
  const [anomalies,   setAnomalies]   = useState(null);
  const [cashflow,    setCashflow]    = useState(null);
  const [longevity,   setLongevity]   = useState(null);

  const [occupancyMonths, setOccupancyMonths] = useState(12);
  const [yoyYear,         setYoyYear]         = useState(new Date().getFullYear());
  const [behaviorMonths,  setBehaviorMonths]  = useState(12);
  const [threshold,       setThreshold]       = useState(30);
  const [cashflowMonths,  setCashflowMonths]  = useState(12);

  const [loading, setLoading] = useState({ occupancy: true, yoy: true, behavior: true, anomalies: true, cashflow: true, longevity: true });

  const setOneLoading = (key, val) => setLoading(l => ({ ...l, [key]: val }));

  const fetchOccupancy = useCallback(async () => {
    setOneLoading('occupancy', true);
    try {
      const res = await analyticsAPI.getOccupancyTimeline({ months: occupancyMonths });
      setOccupancy(res.data.timeline || []);
    } catch { toast.error('Failed to load occupancy data'); }
    finally { setOneLoading('occupancy', false); }
  }, [occupancyMonths]);

  const fetchYoy = useCallback(async () => {
    setOneLoading('yoy', true);
    try {
      const res = await analyticsAPI.getYearOverYear({ year: yoyYear });
      setYoy(res.data || null);
    } catch { toast.error('Failed to load year-over-year data'); }
    finally { setOneLoading('yoy', false); }
  }, [yoyYear]);

  const fetchBehavior = useCallback(async () => {
    setOneLoading('behavior', true);
    try {
      const res = await analyticsAPI.getPaymentBehavior({ months: behaviorMonths });
      setBehavior(res.data.rooms || []);
    } catch { toast.error('Failed to load payment behavior data'); }
    finally { setOneLoading('behavior', false); }
  }, [behaviorMonths]);

  const fetchAnomalies = useCallback(async () => {
    setOneLoading('anomalies', true);
    try {
      const res = await analyticsAPI.getElectricityAnomalies({ threshold, lookback: 6 });
      setAnomalies(res.data || null);
    } catch { toast.error('Failed to load anomaly data'); }
    finally { setOneLoading('anomalies', false); }
  }, [threshold]);

  const fetchCashFlow = useCallback(async () => {
    setOneLoading('cashflow', true);
    try {
      const res = await analyticsAPI.getCashFlow({ months: cashflowMonths });
      setCashflow(res.data || null);
    } catch { toast.error('Failed to load cash flow data'); }
    finally { setOneLoading('cashflow', false); }
  }, [cashflowMonths]);

  const fetchLongevity = useCallback(async () => {
    setOneLoading('longevity', true);
    try {
      const res = await analyticsAPI.getTenantLongevity();
      setLongevity(res.data || null);
    } catch { toast.error('Failed to load tenant longevity data'); }
    finally { setOneLoading('longevity', false); }
  }, []);

  useEffect(() => { fetchOccupancy(); }, [fetchOccupancy]);
  useEffect(() => { fetchYoy(); }, [fetchYoy]);
  useEffect(() => { fetchBehavior(); }, [fetchBehavior]);
  useEffect(() => { fetchAnomalies(); }, [fetchAnomalies]);
  useEffect(() => { fetchCashFlow(); }, [fetchCashFlow]);
  useEffect(() => { fetchLongevity(); }, [fetchLongevity]);

  /* merge yoy data for chart */
  const yoyChartData = yoy ? yoy.current.map((cur, i) => ({
    month: cur.label,
    'This Year':  (parseFloat(cur.rent || 0) + parseFloat(cur.electricity || 0)),
    'Last Year':  yoy.previous[i] ? (parseFloat(yoy.previous[i].rent || 0) + parseFloat(yoy.previous[i].electricity || 0)) : 0,
  })) : [];

  /* behavior months labels */
  const behaviorMonthLabels = behavior[0]?.months?.map(m => m.label) ?? [];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900 dark:text-white flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-primary-500" />
          Analytics
        </h1>
        <p className="text-secondary-500 dark:text-secondary-400 text-sm mt-1">
          Advanced insights — occupancy, trends, payment behaviour and electricity anomalies
        </p>
      </div>

      {/* ───────── 1. OCCUPANCY TIMELINE ───────── */}
      <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
        <div className="card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="font-semibold text-secondary-900 dark:text-white">Occupancy Rate Timeline</h2>
                <p className="text-xs text-secondary-500 dark:text-secondary-400">% of rooms occupied each month</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={occupancyMonths}
                onChange={e => setOccupancyMonths(Number(e.target.value))}
                className="input-field text-sm py-1.5"
              >
                <option value={6}>Last 6 months</option>
                <option value={12}>Last 12 months</option>
                <option value={24}>Last 24 months</option>
              </select>
              <button onClick={fetchOccupancy} className="btn-secondary p-2"><RefreshCw className="w-4 h-4" /></button>
            </div>
          </div>

          {loading.occupancy ? (
            <div className="h-56 flex items-center justify-center"><div className="loading-spinner" /></div>
          ) : occupancy.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-secondary-400">No occupancy data available</div>
          ) : (
            <>
              {/* stat pills */}
              <div className="flex flex-wrap gap-3 mb-4">
                {[
                  { label: 'Avg Occupancy', value: `${(occupancy.reduce((s, d) => s + parseFloat(d.rate || 0), 0) / occupancy.length).toFixed(1)}%`, color: 'text-blue-400' },
                  { label: 'Peak',          value: `${Math.max(...occupancy.map(d => d.rate)).toFixed(1)}%`,                                       color: 'text-emerald-400' },
                  { label: 'Lowest',        value: `${Math.min(...occupancy.map(d => d.rate)).toFixed(1)}%`,                                       color: 'text-amber-400' },
                ].map(s => (
                  <div key={s.label} className="bg-secondary-100 dark:bg-secondary-700/50 rounded-lg px-3 py-1.5">
                    <span className="text-xs text-secondary-500 dark:text-secondary-400">{s.label}: </span>
                    <span className={`text-sm font-bold ${s.color}`}>{s.value}</span>
                  </div>
                ))}
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={occupancy} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="occGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#3B82F6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.4} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                  <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="rate" name="Occupancy %" stroke="#3B82F6" strokeWidth={2} fill="url(#occGrad)" dot={{ r: 3, fill: '#3B82F6' }} activeDot={{ r: 5 }} />
                </AreaChart>
              </ResponsiveContainer>
            </>
          )}
        </div>
      </motion.section>

      {/* ───────── 2. YEAR-OVER-YEAR ───────── */}
      <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <div className="card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h2 className="font-semibold text-secondary-900 dark:text-white">Year-over-Year Comparison</h2>
                <p className="text-xs text-secondary-500 dark:text-secondary-400">Total income — rent + electricity, current vs previous year</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={yoyYear}
                onChange={e => setYoyYear(Number(e.target.value))}
                className="input-field text-sm py-1.5"
              >
                {[0, 1, 2].map(offset => {
                  const y = new Date().getFullYear() - offset;
                  return <option key={y} value={y}>{y}</option>;
                })}
              </select>
              <button onClick={fetchYoy} className="btn-secondary p-2"><RefreshCw className="w-4 h-4" /></button>
            </div>
          </div>

          {loading.yoy ? (
            <div className="h-56 flex items-center justify-center"><div className="loading-spinner" /></div>
          ) : !yoy ? (
            <div className="h-56 flex items-center justify-center text-secondary-400">No year-over-year data available</div>
          ) : (
            <>
              {/* growth pill */}
              <div className="flex flex-wrap gap-3 mb-4">
                {[
                  { label: `${yoy.current_year} Total`, value: formatCurrency(yoy.totals?.current || 0), color: 'text-purple-400' },
                  { label: `${yoy.previous_year} Total`, value: formatCurrency(yoy.totals?.previous || 0), color: 'text-secondary-400' },
                  {
                    label: 'Growth',
                    value: `${yoy.totals?.growth >= 0 ? '+' : ''}${parseFloat(yoy.totals?.growth || 0).toFixed(1)}%`,
                    color: yoy.totals?.growth >= 0 ? 'text-emerald-400' : 'text-red-400',
                  },
                ].map(s => (
                  <div key={s.label} className="bg-secondary-100 dark:bg-secondary-700/50 rounded-lg px-3 py-1.5">
                    <span className="text-xs text-secondary-500 dark:text-secondary-400">{s.label}: </span>
                    <span className={`text-sm font-bold ${s.color}`}>{s.value}</span>
                  </div>
                ))}
              </div>
              <ResponsiveContainer width="100%" height={270}>
                <ComposedChart data={yoyChartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.4} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                  <YAxis tickFormatter={v => `£${v}`} tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                  <Tooltip content={<CustomTooltip />} formatter={(v) => formatCurrency(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Last Year" fill="#6B7280" opacity={0.6} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="This Year" fill="#8B5CF6" opacity={0.9} radius={[3, 3, 0, 0]} />
                  <Line type="monotone" dataKey="This Year" stroke="#A78BFA" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </>
          )}
        </div>
      </motion.section>

      {/* ───────── 3. PER-ROOM PAYMENT BEHAVIOUR ───────── */}
      <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
        <div className="card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <Building className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h2 className="font-semibold text-secondary-900 dark:text-white">Per-Room Payment Behaviour</h2>
                <p className="text-xs text-secondary-500 dark:text-secondary-400">Historical payment heatmap — green = paid, red = missed</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={behaviorMonths}
                onChange={e => setBehaviorMonths(Number(e.target.value))}
                className="input-field text-sm py-1.5"
              >
                <option value={6}>6 months</option>
                <option value={12}>12 months</option>
                <option value={24}>24 months</option>
              </select>
              <button onClick={fetchBehavior} className="btn-secondary p-2"><RefreshCw className="w-4 h-4" /></button>
            </div>
          </div>

          {loading.behavior ? (
            <div className="h-40 flex items-center justify-center"><div className="loading-spinner" /></div>
          ) : behavior.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-secondary-400">No payment behaviour data</div>
          ) : (
            <>
              {/* legend */}
              <div className="flex flex-wrap gap-3 mb-4">
                {Object.entries(CELL_COLORS).filter(([k]) => k !== 'future').map(([key, bg]) => (
                  <span key={key} className="flex items-center gap-1.5 text-xs text-secondary-500 dark:text-secondary-400">
                    <span className={`inline-block w-3 h-3 rounded-sm ${bg}`} />
                    {CELL_LABEL[key]}
                  </span>
                ))}
              </div>

              {/* month headers */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className="text-left pr-3 pb-2 text-secondary-500 dark:text-secondary-400 font-medium w-28 min-w-[7rem]">Room</th>
                      {behaviorMonthLabels.map(lbl => (
                        <th key={lbl} className="px-0.5 pb-2 text-center text-secondary-500 dark:text-secondary-400 font-medium min-w-[2rem]">{lbl}</th>
                      ))}
                      <th className="pl-3 pb-2 text-center text-secondary-500 dark:text-secondary-400 font-medium min-w-[3.5rem]">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {behavior.map((room, ri) => (
                      <motion.tr
                        key={room.room_id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: ri * 0.03 }}
                      >
                        <td className="pr-3 py-1 font-medium text-secondary-700 dark:text-secondary-300 whitespace-nowrap">
                          Room {room.room_number}
                        </td>
                        {room.months.map((m, mi) => (
                          <td key={mi} className="px-0.5 py-1">
                            <div
                              title={`${m.label}: ${CELL_LABEL[m.status]}`}
                              className={`w-7 h-7 rounded-md mx-auto flex items-center justify-center ${CELL_COLORS[m.status]} opacity-90 hover:opacity-100 transition-opacity cursor-default`}
                            />
                          </td>
                        ))}
                        <td className="pl-3 py-1 text-center">
                          <span className={`font-bold ${room.score >= 80 ? 'text-emerald-400' : room.score >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                            {room.score}%
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* bar chart overview */}
              <div className="mt-6">
                <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-2">Payment Score by Room</p>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={behavior} margin={{ top: 0, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.4} />
                    <XAxis dataKey="room_number" tick={{ fontSize: 11, fill: '#9CA3AF' }} tickFormatter={v => `R${v}`} />
                    <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                    <Tooltip content={<CustomTooltip />} formatter={v => `${v}%`} />
                    <Bar dataKey="score" name="Payment Score %" radius={[4, 4, 0, 0]}
                      fill="#10B981"
                      label={{ position: 'top', formatter: (v) => `${v}%`, fontSize: 10, fill: '#9CA3AF' }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      </motion.section>

      {/* ───────── 4. ELECTRICITY ANOMALY ALERTS ───────── */}
      <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <div className="card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h2 className="font-semibold text-secondary-900 dark:text-white">Electricity Cost Anomaly Alerts</h2>
                <p className="text-xs text-secondary-500 dark:text-secondary-400">Rooms where usage deviates significantly from their 3-month rolling average</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={threshold}
                onChange={e => setThreshold(Number(e.target.value))}
                className="input-field text-sm py-1.5"
              >
                <option value={20}>≥ 20% deviation</option>
                <option value={30}>≥ 30% deviation</option>
                <option value={50}>≥ 50% deviation</option>
              </select>
              <button onClick={fetchAnomalies} className="btn-secondary p-2"><RefreshCw className="w-4 h-4" /></button>
            </div>
          </div>

          {loading.anomalies ? (
            <div className="h-40 flex items-center justify-center"><div className="loading-spinner" /></div>
          ) : !anomalies ? (
            <div className="h-40 flex items-center justify-center text-secondary-400">No anomaly data available</div>
          ) : (
            <>
              {/* summary pills */}
              <div className="flex flex-wrap gap-3 mb-5">
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-1.5 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-sm font-bold text-red-400">{anomalies.total_alerts} alert{anomalies.total_alerts !== 1 ? 's' : ''}</span>
                </div>
                <div className="bg-secondary-100 dark:bg-secondary-700/50 rounded-lg px-3 py-1.5">
                  <span className="text-xs text-secondary-500 dark:text-secondary-400">Threshold: </span>
                  <span className="text-sm font-bold text-amber-400">≥ {anomalies.threshold}%</span>
                </div>
              </div>

              {anomalies.anomalies?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-secondary-400">
                  <CheckCircle className="w-10 h-10 mb-3 text-emerald-400 opacity-70" />
                  <p className="font-medium">No anomalies detected at this threshold</p>
                  <p className="text-sm mt-1">Try lowering the threshold for more sensitivity</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {anomalies.anomalies.map((a, i) => {
                    const severity = Math.abs(a.deviation_pct) >= 50 ? 'critical' : Math.abs(a.deviation_pct) >= 35 ? 'high' : 'medium';
                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl bg-secondary-100 dark:bg-secondary-700/40 border border-secondary-200 dark:border-secondary-700"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <div className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide ${SEVERITY_COLORS[severity]}`}>
                            {severity}
                          </div>
                          <div>
                            <p className="font-semibold text-secondary-900 dark:text-white text-sm">
                              Room {a.room_number} — {a.month_label}
                            </p>
                            <p className="text-xs text-secondary-500 dark:text-secondary-400">
                              {a.units_consumed} units&nbsp;·&nbsp;avg {parseFloat(a.avg_units || 0).toFixed(1)} units
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <div>
                            <p className="text-xs text-secondary-500 dark:text-secondary-400">Deviation</p>
                            <p className={`font-bold ${a.deviation_pct > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                              {a.deviation_pct > 0 ? '+' : ''}{parseFloat(a.deviation_pct).toFixed(1)}%
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-secondary-500 dark:text-secondary-400">Amount</p>
                            <p className="font-bold text-secondary-900 dark:text-white">{formatCurrency(a.total_amount)}</p>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}

              {/* per-room summary chart */}
              {anomalies.summaries?.length > 0 && (
                <div className="mt-6">
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-2">Average Units per Room (Last 6 months)</p>
                  <ResponsiveContainer width="100%" height={170}>
                    <BarChart data={anomalies.summaries} margin={{ top: 0, right: 10, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.4} />
                      <XAxis dataKey="room_number" tick={{ fontSize: 11, fill: '#9CA3AF' }} tickFormatter={v => `R${v}`} />
                      <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="avg_units" name="Avg Units" fill="#F59E0B" opacity={0.85} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </div>
      </motion.section>

      {/* ─── 5. Cash Flow: Projected vs Collected ────────────────── */}
      <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
        <div className="card p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-primary-500" />
                Cash Flow — Projected vs Collected
              </h2>
              <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-0.5">Monthly potential income vs actual payments received</p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={cashflowMonths}
                onChange={e => setCashflowMonths(Number(e.target.value))}
                className="input-field text-sm py-1.5"
              >
                <option value={6}>6 months</option>
                <option value={12}>12 months</option>
                <option value={24}>24 months</option>
              </select>
              <button onClick={fetchCashFlow} className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors">
                <RefreshCw className="w-4 h-4 text-secondary-500" />
              </button>
            </div>
          </div>

          {loading.cashflow ? (
            <div className="flex items-center justify-center h-48"><div className="loading-spinner" /></div>
          ) : cashflow ? (
            <>
              {/* KPI row */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-secondary-50 dark:bg-secondary-700/50 rounded-xl p-3 text-center">
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Monthly Projected</p>
                  <p className="text-lg font-bold text-primary-600 dark:text-primary-400">{formatCurrency(cashflow.monthly_projected)}</p>
                </div>
                <div className="bg-secondary-50 dark:bg-secondary-700/50 rounded-xl p-3 text-center">
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Avg Collected</p>
                  <p className="text-lg font-bold text-green-600 dark:text-green-400">{formatCurrency(cashflow.avg_actual)}</p>
                </div>
                <div className="bg-secondary-50 dark:bg-secondary-700/50 rounded-xl p-3 text-center">
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Collection Rate</p>
                  <p className={`text-lg font-bold ${
                    cashflow.collection_rate >= 90 ? 'text-green-600 dark:text-green-400' :
                    cashflow.collection_rate >= 70 ? 'text-amber-600 dark:text-amber-400' :
                    'text-red-600 dark:text-red-400'
                  }`}>{cashflow.collection_rate}%</p>
                </div>
              </div>

              {/* Chart */}
              <ResponsiveContainer width="100%" height={240}>
                <ComposedChart data={cashflow.cashflow} margin={{ top: 4, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.4} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} tickFormatter={v => `£${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="projected" name="Projected" fill="#3B82F6" stroke="#3B82F6" fillOpacity={0.15} strokeWidth={2} dot={false} />
                  <Bar dataKey="actual" name="Collected" fill="#10B981" opacity={0.85} radius={[4, 4, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            </>
          ) : (
            <p className="text-center text-secondary-400 py-8">No cash flow data available</p>
          )}
        </div>
      </motion.section>

      {/* ─── 6. Tenant Longevity ─────────────────────────────────── */}
      <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
        <div className="card p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary-500" />
                Tenant Longevity
              </h2>
              <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-0.5">How long each current tenant has been in their room</p>
            </div>
            <button onClick={fetchLongevity} className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors">
              <RefreshCw className="w-4 h-4 text-secondary-500" />
            </button>
          </div>

          {loading.longevity ? (
            <div className="flex items-center justify-center h-48"><div className="loading-spinner" /></div>
          ) : longevity && longevity.tenants?.length > 0 ? (
            <>
              {/* KPI + tier breakdown */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
                <div className="bg-secondary-50 dark:bg-secondary-700/50 rounded-xl p-3 text-center col-span-2 sm:col-span-1">
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Avg Tenure</p>
                  <p className="text-lg font-bold text-primary-600 dark:text-primary-400">{longevity.avg_months}mo</p>
                </div>
                {[['new', 'New', 'text-sky-500', '<6mo'], ['settled', 'Settled', 'text-blue-500', '6–12mo'], ['established', 'Established', 'text-violet-500', '1–2yr'], ['long-term', 'Long-Term', 'text-emerald-500', '2yr+']].map(([tier, label, color, range]) => (
                  <div key={tier} className="bg-secondary-50 dark:bg-secondary-700/50 rounded-xl p-3 text-center">
                    <p className={`text-xl font-bold ${color}`}>
                      {longevity.tenants.filter(t => t.tier === tier).length}
                    </p>
                    <p className="text-xs text-secondary-500 dark:text-secondary-400">{label}</p>
                    <p className="text-xs text-secondary-400">{range}</p>
                  </div>
                ))}
              </div>

              {/* Bar chart */}
              <ResponsiveContainer width="100%" height={Math.max(longevity.tenants.length * 36, 160)}>
                <BarChart
                  data={longevity.tenants}
                  layout="vertical"
                  margin={{ top: 0, right: 40, left: 10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.4} horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#9CA3AF' }} tickFormatter={v => `${v}mo`} />
                  <YAxis type="category" dataKey="room_number" tick={{ fontSize: 11, fill: '#9CA3AF' }} width={40} tickFormatter={v => `R${v}`} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="bg-secondary-800 border border-secondary-700 rounded-lg p-3 text-sm shadow-xl">
                          <p className="font-semibold text-white mb-1">Room {d.room_number}</p>
                          <p className="text-secondary-300">{d.tenant_name}</p>
                          <p className="text-primary-400">{d.months_tenancy} months tenancy</p>
                          <p className="text-secondary-400 text-xs capitalize">{d.tier}</p>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="months_tenancy" name="Months" radius={[0, 4, 4, 0]}
                    fill="#6366F1"
                    label={{ position: 'right', fontSize: 11, fill: '#9CA3AF', formatter: v => `${v}mo` }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </>
          ) : (
            <div className="text-center py-8 text-secondary-400">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No occupied rooms with move-in dates recorded</p>
            </div>
          )}
        </div>
      </motion.section>
    </div>
  );
};

export default AnalyticsPage;
